import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './CarryForwardBanner.css';

interface CFItem {
  id: number;
  host: string;
  ip: string;
  port: number;
  protocol: string;
  title: string;
  severity: string;
  vuln_type: string;
  cvss: number;
  filter_reason: string;
  original_scan_id: number;
  original_detected_at: string;
  injection_count: number;
  evidence: string;
}

type Labels = {
  title: string; subtitle: string; intro: string;
  accept: string; acceptAuto: string; dismiss: string;
  acceptAll: string; acceptAllAuto: string;
  reason: string; since: string; scan: string; details: string; carried: string;
};

const L: Record<string, Labels> = {
  de: {
    title: 'Carry-forward-Befunde benötigen eine Entscheidung',
    subtitle: 'übertragen aus einem früheren Scan – nicht neu verifiziert (Port gefiltert)',
    intro: 'Diese Befunde wurden aus einem früheren Scan übernommen, weil ihr Port inzwischen gefiltert ist und nicht neu geprüft werden konnte. Sie zählen erst als aktiv, wenn du sie bestätigst. „Verwerfen", falls die Exposition weg / behoben ist.',
    accept: 'Akzeptieren',
    acceptAuto: 'Akzeptieren + künftig automatisch',
    dismiss: 'Verwerfen',
    acceptAll: 'Alle akzeptieren',
    acceptAllAuto: 'Alle + künftig automatisch',
    reason: 'Grund', since: 'zuletzt gesehen', scan: 'Scan', details: 'Details / Evidenz',
    carried: 'mal übertragen',
  },
  en: {
    title: 'Carry-forward findings need a decision',
    subtitle: 'carried over from an earlier scan – not freshly verified (port filtered)',
    intro: 'These findings were carried over from an earlier scan because their port is now filtered and could not be re-verified. They do not count as active until you confirm them. Use "Dismiss" if the exposure is gone / was fixed.',
    accept: 'Accept',
    acceptAuto: 'Accept + auto from now on',
    dismiss: 'Dismiss',
    acceptAll: 'Accept all',
    acceptAllAuto: 'All + auto from now on',
    reason: 'Reason', since: 'last seen', scan: 'Scan', details: 'Details / Evidence',
    carried: 'times carried',
  },
  fr: {
    title: 'Des résultats reportés nécessitent une décision',
    subtitle: "reportés d'une analyse antérieure – non revérifiés (port filtré)",
    intro: "Ces résultats ont été reportés d'une analyse antérieure car leur port est désormais filtré et n'a pas pu être revérifié. Ils ne comptent comme actifs qu'après votre confirmation. « Rejeter » si l'exposition a disparu / a été corrigée.",
    accept: 'Accepter',
    acceptAuto: 'Accepter + automatiquement à l’avenir',
    dismiss: 'Rejeter',
    acceptAll: 'Tout accepter',
    acceptAllAuto: 'Tout + automatique',
    reason: 'Raison', since: 'vu la dernière fois', scan: 'Analyse', details: 'Détails / Preuve',
    carried: 'fois reporté',
  },
  it: {
    title: 'Risultati riportati richiedono una decisione',
    subtitle: 'riportati da una scansione precedente – non riverificati (porta filtrata)',
    intro: 'Questi risultati sono stati riportati da una scansione precedente perché la loro porta è ora filtrata e non è stato possibile riverificarli. Contano come attivi solo dopo la conferma. Usa "Scarta" se l’esposizione è sparita / è stata corretta.',
    accept: 'Accetta',
    acceptAuto: 'Accetta + automatico d’ora in poi',
    dismiss: 'Scarta',
    acceptAll: 'Accetta tutto',
    acceptAllAuto: 'Tutto + automatico',
    reason: 'Motivo', since: 'visto l’ultima volta', scan: 'Scansione', details: 'Dettagli / Evidenza',
    carried: 'volte riportato',
  },
};

const CarryForwardBanner: React.FC<{ projectId: number | null }> = ({ projectId }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const t = L[lang] || L.en;

  const [items, setItems] = useState<CFItem[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!projectId) { setItems([]); return; }
    axios.get(`/api/v1/projects/${projectId}/carry-forward-pending`)
      .then(r => setItems(r.data?.pending || []))
      .catch(() => setItems([]));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (!projectId || items.length === 0) return null;

  const decide = async (id: number, action: 'accept' | 'dismiss', auto = false) => {
    setBusy(true);
    try {
      if (action === 'accept') {
        await axios.post(`/api/v1/persistent-findings/${id}/accept`, { auto });
      } else {
        await axios.post(`/api/v1/persistent-findings/${id}/dismiss`, {});
      }
      load();
    } catch { /* keep banner; user can retry */ } finally { setBusy(false); }
  };

  const acceptAll = async (auto: boolean) => {
    setBusy(true);
    try {
      await axios.post(`/api/v1/projects/${projectId}/carry-forward-accept-all`, { auto });
      load();
    } catch { /* noop */ } finally { setBusy(false); }
  };

  return (
    <div className="cf-banner">
      <button type="button" className="cf-header" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="cf-badge">{items.length}</span>
        <span className="cf-titles">
          <span className="cf-title">{t.title}</span>
          <span className="cf-sub">{t.subtitle}</span>
        </span>
        <span className="cf-chevron">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="cf-body">
          <div className="cf-intro">
            <span>{t.intro}</span>
            <div className="cf-bulk">
              <button type="button" disabled={busy} onClick={() => acceptAll(false)}>{t.acceptAll}</button>
              <button type="button" disabled={busy} onClick={() => acceptAll(true)}>{t.acceptAllAuto}</button>
            </div>
          </div>

          {items.map(it => (
            <div key={it.id} className="cf-item">
              <div className="cf-item-main">
                <span className={`cf-sev sev-${(it.severity || 'info').toLowerCase()}`}>{it.severity}</span>
                <span className="cf-item-title">{it.title}</span>
                <span className="cf-loc">{it.host}{it.port > 0 ? `:${it.port}` : ''}</span>
              </div>
              <div className="cf-item-reason">
                {t.reason}: {it.filter_reason}
                {it.original_detected_at && ` · ${t.since} ${new Date(it.original_detected_at).toLocaleDateString()}`}
                {it.original_scan_id > 0 && ` (${t.scan} #${it.original_scan_id})`}
                {it.injection_count > 0 && ` · ${it.injection_count}× ${t.carried}`}
              </div>
              {it.evidence && (
                <details className="cf-ev">
                  <summary>{t.details}</summary>
                  <pre>{it.evidence}</pre>
                </details>
              )}
              <div className="cf-actions">
                <button type="button" disabled={busy} className="cf-accept" onClick={() => decide(it.id, 'accept', false)}>{t.accept}</button>
                <button type="button" disabled={busy} className="cf-accept-auto" onClick={() => decide(it.id, 'accept', true)}>{t.acceptAuto}</button>
                <button type="button" disabled={busy} className="cf-dismiss" onClick={() => decide(it.id, 'dismiss')}>{t.dismiss}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CarryForwardBanner;
